// Stopwatch — count-up stopky pre súťaže (v2): štart od 0, Stop → onStop(sekundy)
// a rodič výsledok uloží (add-mode). Beh prežije reload/zavretie appky — štart je
// v localStorage ako timestamp, čas sa dopočítava z Date.now() (žiadny tikajúci stav).
//
// v32.3 — Stop je OKAMŽITÝ: ťuknutie zmrazí ciferník na nameranom čase (`stoppedAt`,
// tiež v localStorage) a tlačidlo prejde do „Ukladám…"; sieťové uloženie beží až potom.
// Dovtedy stopky ďalej tikali a tlačidlo nijak nereagovalo, kým nedobehla požiadavka —
// na mobile cez pomalšiu sieť to vyzeralo, že sa stopky „zasekli a nedajú zastaviť".
// Keď uloženie zlyhá, nameraný čas ostáva zmrazený (neprirastá čakaním) a dá sa
// „Skúsiť znova" alebo zahodiť.
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { st } from "../../styles.js";
import { BuzerIcon } from "../../BuzerIcons";
import { fmtDur } from "../../lib/format";
import { useWakeLock } from "../../hooks/useWakeLock";

const readTs = (k) => { const v = localStorage.getItem(k); return v ? parseInt(v, 10) : null; };

export function Stopwatch({ id, onStop }) {
  const key = "buzer_stopwatch_" + id;
  const stopKey = key + "_stop";
  const [startTime, setStartTime] = useState(() => readTs(key));
  const [stoppedAt, setStoppedAt] = useState(() => readTs(stopKey));
  const [now, setNow] = useState(() => Date.now());
  const [full, setFull] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");  // chyba uloženia sa musí ukázať V overlayi, nie pod ním
  const savingRef = useRef(false);
  const running = !!startTime && !stoppedAt;
  useWakeLock(running);  // kým stopky bežia, obrazovka nezhasne (inak sa telefón uprostred planku zamkne)
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [running]);

  function start() {
    const t = Date.now();
    localStorage.setItem(key, String(t));
    localStorage.removeItem(stopKey);
    setNow(t); setErr("");
    setStartTime(t); setStoppedAt(null);
    setFull(true);
  }
  function discard() {
    localStorage.removeItem(key);
    localStorage.removeItem(stopKey);
    setStartTime(null); setStoppedAt(null); setFull(false); setErr("");
  }
  function freeze() {
    const t = Date.now();
    localStorage.setItem(stopKey, String(t));
    setStoppedAt(t);
    return t;
  }
  function resume() {
    localStorage.removeItem(stopKey);
    setStoppedAt(null); setNow(Date.now());
  }
  async function stopAndSave() {
    if (savingRef.current) return;  // double-tap guard
    // 1. zmraz čas HNEĎ — ciferník sa zastaví na nameranej hodnote, ešte pred sieťou
    const stopTs = stoppedAt || freeze();
    const sec = Math.max(1, Math.round((stopTs - startTime) / 1000));
    // zabudnuté stopky (napr. cez noc) — pýtaj sa, než sa pripočíta obrí čas; „nie" = beží ďalej
    if (sec > 12 * 3600 && !confirm(`Stopky bežali ${fmtDur(sec)}. Naozaj uložiť celý čas?`)) { resume(); return; }
    // 2. ulož; kým beží požiadavka, tlačidlo je „Ukladám…" a nedá sa ťuknúť znova
    savingRef.current = true; setSaving(true); setErr("");
    try { await onStop(sec); discard(); }
    catch (e) { setErr((e && e.message) || "Nepodarilo sa uložiť. Skús znova."); }
    finally { savingRef.current = false; setSaving(false); }
  }

  if (!startTime) {
    // primárne tlačidlo „Spustiť stopky" — rovnaká podoba ako „Spustiť časovač" v detaile návyku
    // (Timer variant="button"); po spustení sa nižšie prepne na sekundárne „Stopky bežia".
    return (
      <button onClick={start} className="bz-btn bz-btn--primary bz-btn--block" title="Spustiť stopky">
        <BuzerIcon name="stopwatch" size={18} color="currentColor" />Spustiť stopky
      </button>
    );
  }
  const elapsed = Math.max(0, Math.floor(((stoppedAt || now) - startTime) / 1000));
  const clock = fmtDur(elapsed);
  const stateLbl = saving ? "Ukladám…" : stoppedAt ? "Stopky zastavené" : "Stopky bežia";
  return (
    <>
      <button onClick={() => setFull(true)} className="bz-btn bz-btn--secondary bz-btn--block" title="Stopky — otvoriť na celú obrazovku">
        <BuzerIcon name="stopwatch" size={18} color="currentColor" />{stateLbl} · {clock}
      </button>
      {full && createPortal(
        <div style={st.timerFull} onClick={() => { if (!saving) setFull(false); }}>
          <div style={st.timerFullInner} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...st.timerDigits, fontFamily: "var(--font-mono)", color: "var(--text)" }}>{clock}</div>
            <div style={{ ...st.timerFullHint, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {saving ? "Ukladám výsledok…"
                : stoppedAt ? "Zastavené — výsledok sa nepodarilo uložiť"
                : <>Do toho! Stop uloží výsledok <BuzerIcon name="dumbbell" size={16} color="currentColor" /></>}
            </div>
            {err && <div style={{ ...st.err, marginTop: 12 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 22, width: "100%", maxWidth: 340 }}>
              <button disabled={saving} onClick={() => { if (confirm("Zahodiť meranie bez uloženia?")) discard(); }} style={st.ghostBtn}>Zahodiť</button>
              <button disabled={saving} aria-busy={saving || undefined} onClick={stopAndSave} style={{ ...st.primaryBtn, flex: 1 }}>
                {saving ? "Ukladám…" : stoppedAt ? "Skúsiť znova" : "Stop a ulož"}
              </button>
            </div>
            {stoppedAt && !saving
              ? <button onClick={resume} style={{ ...st.linkBtn, marginTop: 12 }}>Pokračovať v meraní</button>
              : <button disabled={saving} onClick={() => setFull(false)} style={{ ...st.linkBtn, marginTop: 12 }}>Skryť (beží ďalej)</button>}
          </div>
        </div>, document.body
      )}
    </>
  );
}
